import React, { Component } from "react";
import { injectIntl } from "react-intl";
import { connect } from "react-redux";
import { Modal, Button, Progress, Alert } from "antd";
import Copyright, { COPYRIGHT_UI_DUE_DATE } from "../upload/Copyright";
import {
  assignRightsMany,
  clearRightsAssignError
} from "../../actions/RightsAction";
import { updateAssetRightsInList } from "../../actions/ClientAction";
import BunkeyAPI from "../../api/BunkeyAPI";
import {
  RIGHTS_BATCH_CONFIRMATION_THRESHOLD,
  RIGHTS_BATCH_MAX
} from "../../constants/rights-constants";
import "./RightsManager.css";

// Progreso simulado sobre un PUT síncrono (decisión tomada con el usuario):
// el backend de assignRightsMany procesa todo dentro de un solo request, no
// hay operationId ni socket que reportar mientras corre. Esto es puramente
// cosmético — nunca refleja el chunk real que está procesando el servidor.
const SIMULATED_PROGRESS_STEP_MS = 220;

// Cuántos nombres de archivo se listan antes de resumir el resto en "y N más".
// La selección puede llegar a BATCH_SYNC_LIMIT (5000) piezas: pintar un <li>
// por cada una llenaría el modal de nodos sin que nadie los lea. La lista
// además scrollea, así que el tope es sobre el DOM, no sobre lo visible.
// Punto 5 de Ricardo: en el resumen se listan 3 nombres y el resto se abre con
// "Ver todos". Antes se volcaban 50 de una y el modal se hacía larguísimo con
// una selección normal, tapando el total y el selector de estado.
const SCOPE_FILES_VISIBLE = 3;

class RightsAssignModal extends Component {
  state = {
    pendingRights: null,
    progress: 0,
    // Resumen que devuelve el backend cuando la selección trae carpetas sin
    // conteo propio. Ver fetchScopeIfNeeded.
    remoteScope: null,
    loadingScope: false,
    // "Ver todos" del resumen de selección.
    showAllFiles: false,
    showAllFolders: false
  };

  // Los dos llamadores montan el modal con `{showModal && <RightsAssignModal
  // visible />}`, así que nace visible y NUNCA hay transición false -> true.
  // Sin esto el alcance no se pedía nunca y el total salía NaN.
  componentDidMount() {
    // El error de un intento anterior no describe a esta selección.
    this.props.clearRightsAssignError();
    if (this.props.visible) {
      this.fetchScopeIfNeeded();
    }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.visible && this.props.visible) {
      this.props.clearRightsAssignError();
      this.fetchScopeIfNeeded();
    }
    if (prevProps.loadingAssign && !this.props.loadingAssign) {
      clearInterval(this.progressTimer);
      if (this.props.errorAssign) {
        this.setState({ progress: 0 });
      } else {
        // El modal esperaba UNA petición por archivo —de a 8— antes de
        // cerrarse. Con una selección grande quedaba colgado en 90% durante
        // minutos, aunque la asignación ya estaba aplicada en el servidor
        // desde el primer momento. Ahora cierra y el refresco corre solo:
        // actualiza la grilla por redux, así que no le molesta desmontarse.
        this.refreshSelectedRights();
        this.setState({ progress: 100 }, () => {
          setTimeout(() => this.props.onDone(), 300);
        });
      }
    }
  }

  // El lote puede llegar a BATCH_SYNC_LIMIT (5000) piezas. Refrescarlas todas
  // con un solo Promise.all dispararía esa misma cantidad de GET simultáneos
  // contra el gateway; se procesan de a REFRESH_CONCURRENCY para no saturar
  // ni al navegador ni al backend.
  refreshSelectedRights = () => {
    const { selection, workclient, userId, updateAssetRightsInList } = this.props;
    const files = selection.filter(
      ({ kind, data }) =>
        kind !== "folder" &&
        kind !== "bunker" &&
        data &&
        data._id
    );

    const REFRESH_CONCURRENCY = 8;
    // Este refresco sólo sirve para actualizar las fichas YA renderizadas en la
    // grilla, que como mucho son una página. Pedir 5.000 sería castigar a la
    // API para repintar 50. En el Gestor no hace falta ninguna: el árbol y los
    // KPI se recargan enteros con una consulta por nodo.
    const REFRESH_MAX = 120;
    const refreshOne = ({ data }) =>
      BunkeyAPI({
        url: `/clients/${workclient}/rights/assets/${data._id}?userid=${userId}`,
        method: "GET"
      })
        .then(resp => updateAssetRightsInList(data._id, resp.data))
        .catch(() => null);

    const aRefrescar = files.slice(0, REFRESH_MAX);
    let cursor = 0;
    const runNextChunk = () => {
      if (cursor >= aRefrescar.length) return Promise.resolve();
      const chunk = aRefrescar.slice(cursor, cursor + REFRESH_CONCURRENCY);
      cursor += REFRESH_CONCURRENCY;
      return Promise.all(chunk.map(refreshOne)).then(runNextChunk);
    };

    return runNextChunk();
  };

  componentWillUnmount() {
    clearInterval(this.progressTimer);
  }

  /**
   * Pide al backend cuántas piezas alcanza la selección.
   *
   * Hace falta sólo cuando el modal se abre desde la barra de selección
   * múltiple de la grilla: ahí una carpeta llega sin `totalPieces`, porque ese
   * dato lo calcula el árbol del Gestor. Sin él no se puede decir cuántos
   * archivos se van a modificar antes de aplicar.
   *
   * Si la selección no tiene carpetas, o si ya vienen con su conteo (el caso
   * del Gestor), no se pide nada y el resumen se arma en el cliente como antes.
   */
  fetchScopeIfNeeded() {
    const { selection, workclient, userId } = this.props;

    const necesitaConteo = (selection || []).some(
      ({ kind, data }) =>
        kind === "folder" && (!data || data.totalPieces === undefined)
    );

    if (!necesitaConteo) {
      this.setState({ remoteScope: null, loadingScope: false });
      return;
    }

    this.setState({ loadingScope: true, remoteScope: null });
    BunkeyAPI({
      url: `clients/${workclient}/rights/scope`,
      method: "PUT",
      data: {
        userid: userId,
        objects: selection.map(({ data }) => data._id)
      }
    })
      .then(resp => this.setState({ remoteScope: resp.data, loadingScope: false }))
      .catch(() => this.setState({ remoteScope: null, loadingScope: false }));
  }

  buildScope() {
    const { selection } = this.props;
    const { remoteScope } = this.state;

    // El backend ya resolvió el alcance: se usa tal cual. Su contentTotal está
    // deduplicado —un asset dentro de dos carpetas anidadas se cuenta una vez—
    // así que es más fiel que sumar los totales de cada carpeta.
    if (remoteScope) {
      return {
        contentTotal: remoteScope.contentTotal,
        outOfType: remoteScope.outOfType,
        folderRows: (remoteScope.folders || []).map(folder => ({
          name: folder.name,
          count: folder.count
        })),
        fileRows: (remoteScope.files || []).map(file => ({
          id: file._id,
          name: file.name
        }))
      };
    }

    let contentTotal = 0;
    let outOfType = 0;
    const folderRows = [];
    const fileRows = [];

    selection.forEach(({ kind, data }) => {
      if (kind === "folder") {
        // Una carpeta que viene de la grilla no trae conteo propio: ese dato lo
        // arma el árbol del Gestor. Sumar undefined daba "NaN piezas". El número
        // correcto llega por /rights/scope; acá sólo se evita el NaN mientras
        // tanto, o si esa llamada falla.
        const piezas = Number(data.totalPieces) || 0;
        contentTotal += piezas;
        outOfType += Number(data.outOfModuleCount) || 0;
        folderRows.push({ name: data.name, count: piezas });
      } else {
        if (data.isContent === false) {
          outOfType += 1;
        } else {
          contentTotal += 1;
          // El nombre se muestra en la lista del modal para que se vea a qué
          // archivos se les va a escribir la fecha, no sólo cuántos son.
          fileRows.push({ id: data._id, name: data.name || data.originalName || "" });
        }
      }
    });

    return { contentTotal, outOfType, folderRows, fileRows };
  }

  handleCopyright = ({ copyright }) => {
    this.setState({ pendingRights: copyright });
  };

  handleSubmit = () => {
    const { contentTotal } = this.buildScope();
    if (contentTotal >= RIGHTS_BATCH_CONFIRMATION_THRESHOLD) {
      Modal.confirm({
        title: this.props.intl.formatMessage(
          { id: "rights_manager_confirm_title" },
          { count: contentTotal }
        ),
        onOk: () => this.doSubmit()
      });
      return;
    }
    this.doSubmit();
  };

  /**
   * Traduce lo que emite Copyright al payload que espera el backend.
   *
   * Copyright emite `createDate` y un `uiState` que sólo le sirve a la UI;
   * RightsService.buildRightsSet lee `createdDate` y descarta en silencio
   * cualquier clave que no conozca. Sin esta traducción la fecha de inicio del
   * período nunca se guardaba en la asignación por lote —el detalle de un asset
   * sí la mandaba bien (CollectionMiddleViewAssetInfo.js:632), de ahí que el
   * mismo campo funcionara en una pantalla y no en la otra.
   */
  buildRightsPayload(pendingRights) {
    const { createDate, uiState, incomplete, ...rest } = pendingRights;
    const payload = { ...rest };
    if (createDate !== undefined) payload.createdDate = createDate;
    return payload;
  }

  doSubmit() {
    const { selection, workclient, userId, assignRightsMany } = this.props;
    const { pendingRights } = this.state;
    if (!pendingRights) return;

    const objectIds = [];
    selection.forEach(({ data }) => objectIds.push(data._id));

    this.setState({ progress: 8 });
    this.progressTimer = setInterval(() => {
      this.setState(prev => ({ progress: Math.min(prev.progress + 7, 90) }));
    }, SIMULATED_PROGRESS_STEP_MS);

    assignRightsMany(
      workclient,
      userId,
      objectIds,
      this.buildRightsPayload(pendingRights),
      this.props.intl
    );
  }

  render() {
    const {
      visible,
      onClose,
      intl,
      loadingAssign,
      errorAssign,
      errorAssignCode
    } = this.props;
    const { contentTotal, outOfType, folderRows, fileRows } = this.buildScope();
    const {
      showAllFiles,
      showAllFolders,
      pendingRights: derechosPendientes
    } = this.state;
    // Se podía elegir "Habilitado, con vencimiento", dejar la fecha vacía y
    // aplicar igual: se guardaba como habilitado SIN vencimiento, la columna
    // quedaba en "—" y la única fecha visible era la de inicio.
    const faltaVencimiento = Boolean(
      derechosPendientes &&
        (derechosPendientes.incomplete ||
          (derechosPendientes.uiState === COPYRIGHT_UI_DUE_DATE &&
            !derechosPendientes.dueDate))
    );
    const visibleFiles = showAllFiles
      ? fileRows
      : fileRows.slice(0, SCOPE_FILES_VISIBLE);
    const hiddenFiles = fileRows.length - visibleFiles.length;
    // Pedido de Ricardo: con "Seleccionar todo" en una carpeta grande la lista
    // de carpetas salía entera (cientos de filas) antes que los archivos. Ahora
    // carpetas y archivos van en grupos separados, cada uno con los 3 primeros
    // y "Ver más" / "Ver menos".
    const visibleFolders = showAllFolders
      ? folderRows
      : folderRows.slice(0, SCOPE_FILES_VISIBLE);
    const hiddenFolders = folderRows.length - visibleFolders.length;
    // Fuera de la lista, que tiene alto máximo con scroll: así "Ver menos"
    // queda a la vista aunque se hayan desplegado cientos de filas.
    const toggle = (expanded, hidden, stateKey) => (
      <div className="rm-scope-more">
        <Button
          type="link"
          size="small"
          className="rm-scope-toggle"
          onClick={() => this.setState({ [stateKey]: !expanded })}
        >
          {expanded
            ? intl.formatMessage({ id: "rights_manager_scope_files_less" })
            : intl.formatMessage(
                { id: "rights_manager_scope_files_all" },
                { count: hidden }
              )}
        </Button>
      </div>
    );
    const { pendingRights, progress, loadingScope } = this.state;

    return (
      <Modal
        wrapClassName="rm-assign-modal"
        title={intl.formatMessage({ id: "rights_manager_assign_title" })}
        visible={visible}
        onCancel={loadingAssign ? undefined : onClose}
        closable={!loadingAssign}
        maskClosable={!loadingAssign}
        footer={[
          <Button key="cancel" onClick={onClose} disabled={loadingAssign}>
            {intl.formatMessage({ id: "cancel_button" })}
          </Button>,
          <Button
            key="submit"
            type="primary"
            // Mientras se resuelve el alcance el conteo es 0: aplicar en ese
            // momento mostraría "Aplicar a 0 piezas" y dispararía el lote sin
            // que nadie sepa sobre cuántos archivos.
            disabled={!pendingRights || loadingScope || faltaVencimiento}
            loading={loadingAssign || loadingScope}
            onClick={this.handleSubmit}
          >
            {intl.formatMessage(
              { id: "rights_manager_assign_submit" },
              { count: contentTotal }
            )}
          </Button>
        ]}
      >
        <div className="rm-scope" style={{ marginBottom: 16 }}>
          {/* Pedido de Ricardo: ordenado por categorías — primero las piezas
              que se van a modificar (el total), después carpetas y archivos
              elegidos, y al final lo que no aplica por tipo. */}
          <div className="rm-scope-row tot top">
            <span>{intl.formatMessage({ id: "rights_manager_scope_total" })}</span>
            <b>
              {loadingScope
                ? "—"
                : intl.formatMessage(
                    { id: "rights_manager_pieces_count" },
                    { count: contentTotal }
                  )}
            </b>
          </div>
          {folderRows.length > 0 && (
            <div className="rm-scope-group">
              <div className="rm-scope-row rm-scope-head">
                <span>{intl.formatMessage({ id: "rights_manager_scope_folders" })}</span>
                <b>{folderRows.length}</b>
              </div>
              <ul className="rm-scope-files">
                {visibleFolders.map((row, index) => (
                  <li className="rm-scope-folder" key={`${row.name}-${index}`} title={row.name}>
                    <span>{row.name}</span>
                    {/* Mientras el backend resuelve el alcance no hay conteo
                        real: mostrar 0 sería tan engañoso como el NaN que
                        había antes. */}
                    <b>{loadingScope ? "—" : row.count}</b>
                  </li>
                ))}
              </ul>
              {(hiddenFolders > 0 || showAllFolders) &&
                toggle(showAllFolders, hiddenFolders, "showAllFolders")}
            </div>
          )}
          {fileRows.length > 0 && (
            <div className="rm-scope-group">
              <div className="rm-scope-row rm-scope-head">
                <span>{intl.formatMessage({ id: "rights_manager_scope_files" })}</span>
                <b>{fileRows.length}</b>
              </div>
              <ul className="rm-scope-files">
                {visibleFiles.map(file => (
                  <li key={file.id} title={file.name}>
                    {file.name}
                  </li>
                ))}
              </ul>
              {(hiddenFiles > 0 || showAllFiles) &&
                toggle(showAllFiles, hiddenFiles, "showAllFiles")}
            </div>
          )}
          {outOfType > 0 && (
            <div className="rm-scope-group">
              <div className="rm-scope-row rm-scope-head">
                {/* Sin signo menos: el total NO los resta —el conteo de cada
                    carpeta ya viene sin ellos—, así que el "-689" hacía pensar
                    que faltaba una resta y que el total estaba mal. */}
                <span>{intl.formatMessage({ id: "rights_manager_scope_out_of_type" })}</span>
                <b style={{ color: "rgba(0,0,0,.45)" }}>{outOfType}</b>
              </div>
            </div>
          )}
        </div>

        <div className="rm-copyright-editor">
          <Copyright
            intl={intl}
            handleCopyright={this.handleCopyright}
            allowLicenseFile
          />
        </div>

        {/* Qué falta lo dice el propio widget (Copyright), igual que en la
            subida y en el panel del archivo. */}

        {errorAssign && !loadingAssign && (
          <Alert
            style={{ marginTop: 16 }}
            type={errorAssignCode === "NoChanges" ? "warning" : "error"}
            showIcon
            message={
              errorAssignCode === "RightsBatchTooLarge"
                ? intl.formatMessage(
                    { id: "rights_manager_assign_too_large" },
                    { count: contentTotal, limit: RIGHTS_BATCH_MAX }
                  )
                : errorAssignCode === "NothingUpdated"
                ? intl.formatMessage({ id: "rights_manager_assign_nothing_updated" })
                : errorAssignCode === "NoChanges"
                ? intl.formatMessage({ id: "rights_manager_assign_no_changes" })
                : errorAssignCode === "EmptySelection"
                ? intl.formatMessage({ id: "rights_manager_assign_empty_selection" })
                : intl.formatMessage({ id: "rights_manager_assign_error" })
            }
          />
        )}

        {loadingAssign && (
          <div style={{ marginTop: 16 }}>
            <Progress percent={progress} showInfo={false} />
            <div className="rm-progress-note">
              {intl.formatMessage({ id: "rights_manager_assign_progress_note" })}
            </div>
          </div>
        )}
      </Modal>
    );
  }
}

const mapStateToProps = state => ({
  loadingAssign: state.rights.loadingAssign,
  errorAssign: state.rights.errorAssign,
  errorAssignCode: state.rights.errorAssignCode
});

export default injectIntl(
  connect(mapStateToProps, {
    assignRightsMany,
    updateAssetRightsInList,
    clearRightsAssignError
  })(RightsAssignModal)
);
